import React, { useState } from 'react';
import { ChevronRight } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_OPTIONS = ['Coach', 'Player', 'Parent', 'Program Leader', 'Other'];
const SERVICE_OPTIONS = [
  'Player Film Audit',
  'Coach Film Audit',
  'Offensive System Review',
  'Parent Athlete Evaluation',
  'Program Builder Review',
  'Custom Football Consultation',
  'General Inquiry',
];
const FOLLOWUP_OPTIONS = ['Email', 'Text', 'Phone Call'];

const initialForm = {
  name: '',
  email: '',
  phone: '',
  role: '',
  service_requested: '',
  team_or_org: '',
  competitive_level: '',
  biggest_issue: '',
  film_link: '',
  specific_plays: '',
  how_heard: '',
  num_clips: '',
  timeline: '',
};

export default function IntakeForm({ preselectedService, preselectedRole }) {
  const [form, setForm] = useState({
    ...initialForm,
    service_requested: preselectedService || '',
    role: preselectedRole || '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/intake/submit`, form);
      setSubmitted(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        setError(detail.map((d) => d.msg).join(', '));
      } else {
        setError(detail || 'Something went wrong. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div data-testid="intake-success" className="bg-gray-900 border border-gray-700 rounded-2xl p-10 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-green-600 rounded-full mb-6">
          <ChevronRight className="h-8 w-8 text-white" />
        </div>
        <h3 className="text-2xl font-bold text-white mb-4">Request Received</h3>
        <p className="text-gray-400 leading-relaxed max-w-lg mx-auto">
          Your request has been received. Weldon will review your information and follow up directly to discuss the best audit option, package, pricing, and next steps.
        </p>
      </div>
    );
  }

  const inputClass = "w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all text-sm";
  const labelClass = "block text-sm font-medium text-gray-300 mb-1.5";

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid md:grid-cols-2 gap-5">
        <div>
          <label className={labelClass}>Name *</label>
          <input data-testid="intake-name" type="text" name="name" required value={form.name} onChange={handleChange} className={inputClass} placeholder="Full name" />
        </div>
        <div>
          <label className={labelClass}>Email *</label>
          <input data-testid="intake-email" type="email" name="email" required value={form.email} onChange={handleChange} className={inputClass} placeholder="you@email.com" />
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <div>
          <label className={labelClass}>Phone *</label>
          <input data-testid="intake-phone" type="tel" name="phone" required value={form.phone} onChange={handleChange} className={inputClass} placeholder="(555) 123-4567" />
        </div>
        <div>
          <label className={labelClass}>Role *</label>
          <select data-testid="intake-role" name="role" required value={form.role} onChange={handleChange} className={inputClass}>
            <option value="">Select role</option>
            {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <div>
          <label className={labelClass}>Service Interested In *</label>
          <select data-testid="intake-service" name="service_requested" required value={form.service_requested} onChange={handleChange} className={inputClass}>
            <option value="">Select service</option>
            {SERVICE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <label className={labelClass}>Team, School, or Organization *</label>
          <input data-testid="intake-team" type="text" name="team_or_org" required value={form.team_or_org} onChange={handleChange} className={inputClass} placeholder="Organization name" />
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <div>
          <label className={labelClass}>Position or Role</label>
          <input type="text" name="competitive_level" value={form.competitive_level} onChange={handleChange} className={inputClass} placeholder="e.g. OC, QB, Safety, Parent" />
        </div>
        <div>
          <label className={labelClass}>Film Link or Website Link</label>
          <input type="url" name="film_link" value={form.film_link} onChange={handleChange} className={inputClass} placeholder="https://hudl.com/..." />
        </div>
      </div>

      <div>
        <label className={labelClass}>What do you need help with? *</label>
        <textarea data-testid="intake-issue" name="biggest_issue" required rows={3} value={form.biggest_issue} onChange={handleChange} className={inputClass + " resize-none"} placeholder="Describe what you need reviewed or evaluated" />
      </div>

      <div>
        <label className={labelClass}>What problem are you trying to solve?</label>
        <textarea name="specific_plays" rows={3} value={form.specific_plays} onChange={handleChange} className={inputClass + " resize-none"} placeholder="Specific problems, plays, concepts, or situations" />
      </div>

      <div>
        <label className={labelClass}>Preferred Follow Up Method</label>
        <select name="how_heard" value={form.how_heard} onChange={handleChange} className={inputClass}>
          <option value="">Select preference</option>
          {FOLLOWUP_OPTIONS.map((f) => <option key={f} value={f}>{f}</option>)}
        </select>
      </div>

      {error && (
        <div data-testid="intake-error" className="bg-red-900/30 border border-red-700 text-red-400 rounded-xl p-4 text-sm">{error}</div>
      )}

      <button
        type="submit"
        data-testid="intake-submit-btn"
        disabled={submitting}
        className="w-full bg-blue-600 hover:bg-blue-700 text-white py-4 rounded-full text-lg font-bold transition-all hover:scale-[1.01] shadow-lg disabled:opacity-60"
      >
        {submitting ? 'Submitting...' : 'Submit Intake Form'}
      </button>
    </form>
  );
}
